import { useContext, useEffect, useState, useRef } from "react";
import { useLocation } from "wouter";
import { motion, AnimatePresence, Transition } from "framer-motion";
import html2canvas from "html2canvas";
import { AuthContext } from "~/utils/useAuth";
import { styled, vh, darkTheme } from "~/ui/style/stitches.config";
import { useSnapshot } from "valtio";
import { localState } from "~/state/state";
import {
  getTodaysSpread,
  getTodaysResult,
  saveDailySpreadResult,
  getAllDailySpreads,
  generateInterpretation,
  DailySpread,
  DailySpreadResult,
  CardResult,
} from "~/api/dailySpreadApi";
import { saveSpreadToJournal } from "~/api/journalApi";
import { trackEvent } from "~/api/analyticsApi";
import { getCardOverviews, CardOverview } from "~/api/guidebookApi";
import { SimpleLoadingIndicator } from "~/pages/loading/LoadingPage";
import { HoloCard } from "~/ui/components/HoloCard";
import { FlippableCard } from "~/ui/components/FlippableCard";
import { isIOSNativeApp } from "~/utils/deviceDetection";
import { ShowcaseCardModal } from "~/ui/components/ShowcaseCardModal";
import { ShareableSpread } from "./ShareableSpread";
import rwsDeck from "~/assets/decks/rider-waite.json";

// Native share bridge the Android app injects; absent on iOS/web (see below).
type MoonlightNativeBridge = {
  shareImage?: (base64: string, text: string, filename: string) => void;
};

const getNativeShareBridge = (): MoonlightNativeBridge | undefined =>
  (window as unknown as { MoonlightNative?: MoonlightNativeBridge }).MoonlightNative;

// Blob -> raw base64 (drops the data: prefix) for the bridge.
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result).split(",")[1] ?? "");
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });

// Get next local midnight as a Date object
const getNextLocalMidnight = (): Date => {
  const now = new Date();
  const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0, 0);
  return tomorrow;
};

// Calculate time remaining until local midnight
const getTimeUntilMidnight = (): string => {
  const now = new Date();
  const midnight = getNextLocalMidnight();

  const diffMs = midnight.getTime() - now.getTime();
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// Predetermined background colors that cycle daily
const BACKGROUND_COLORS = [
  "#daeea1", // light green
  "#ecd8ce", // tan
  "#e9b8ff", // light purple
  "#edc29a", // orange
  "#e6e6e6", // light gray
];

// Get deterministic background color based on date (or random in test mode)
const getBackgroundColor = (isTestMode: boolean): string => {
  if (isTestMode) {
    return BACKGROUND_COLORS[Math.floor(Math.random() * BACKGROUND_COLORS.length)];
  }
  const today = new Date();
  const dayOfYear = Math.floor(
    (today.getTime() - new Date(today.getFullYear(), 0, 0).getTime()) /
    (1000 * 60 * 60 * 24)
  );
  return BACKGROUND_COLORS[dayOfYear % BACKGROUND_COLORS.length];
};

// Pick random cards from the deck
const pickRandomCards = (count: number): CardResult[] => {
  const deck = [...rwsDeck.cards];
  const picked: CardResult[] = [];

  for (let i = 0; i < count; i++) {
    const randomIndex = Math.floor(Math.random() * deck.length);
    const card = deck.splice(randomIndex, 1)[0];
    picked.push({
      name: card.name,
      artwork: card.artwork,
      reversed: Math.random() < 0.3,
    });
  }

  return picked;
};

export const DailySpreadPage = () => {
  const { user } = useContext(AuthContext);
  const [, setLocation] = useLocation();
  const [loading, setLoading] = useState(true);
  const [spread, setSpread] = useState<DailySpread | null>(null);
  const [result, setResult] = useState<DailySpreadResult | null>(null);
  const [cards, setCards] = useState<CardResult[]>([]);
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());
  const [flippingCard, setFlippingCard] = useState<number | null>(null);
  const [cardsPreFlipped, setCardsPreFlipped] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showcasedCardIndex, setShowcasedCardIndex] = useState<number | null>(null);
  const [countdown, setCountdown] = useState(getTimeUntilMidnight());
  const [interpretation, setInterpretation] = useState<string | null>(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [cardOverviews, setCardOverviews] = useState<Map<string, CardOverview>>(new Map());
  const [savedToJournal, setSavedToJournal] = useState(false);
  const [savingToJournal, setSavingToJournal] = useState(false);

  // Test mode: add ?test to URL to skip result check and not save (admin only)
  const isTestMode = window.location.search.includes("test") && !!user?.isAdmin;

  // Track if we've already initialized cards (prevents re-generation on re-renders)
  const initializedRef = useRef(false);

  // Track if we should animate cards flying in (only on fresh spreads, not returning visits)
  const shouldAnimateFlyInRef = useRef(true);

  // Store background color in ref so it doesn't change on re-renders
  const backgroundColorRef = useRef(getBackgroundColor(isTestMode));
  const dailyColor = backgroundColorRef.current;
  const { appDarkMode } = useSnapshot(localState);
  // Dark mode: ignore the daily color and use the app's dark background
  const backgroundColor = appDarkMode ? "var(--colors-appBg)" : dailyColor;

  const isIOSNative = isIOSNativeApp();

  // Ref for the shareable spread (for screenshot)
  const shareableRef = useRef<HTMLDivElement>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  // Update countdown every second
  useEffect(() => {
    const interval = setInterval(() => {
      setCountdown(getTimeUntilMidnight());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // iOS native: hide TopBar header
  useEffect(() => {
    if (isIOSNativeApp()) {
      const topBar = document.querySelector('[data-darkmode]') as HTMLElement;
      if (topBar) {
        topBar.style.display = 'none';
      }

      return () => {
        if (topBar) {
          topBar.style.display = 'flex';
        }
      };
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setLocation("/signup");
      return;
    }

    // Don't re-initialize if we already have cards
    if (initializedRef.current) return;

    trackEvent("/daily-spread/visited_page");

    const fetchData = async () => {
      try {
        // In test mode, skip checking for existing result
        if (!isTestMode) {
          const existingResult = await getTodaysResult();
          if (existingResult) {
            shouldAnimateFlyInRef.current = false; // Don't animate on returning visits
            setResult(existingResult);
            setSpread(existingResult.spread);
            setCards(existingResult.cards);
            setFlippedCards(new Set(existingResult.cards.map((_, i) => i)));
            setCardsPreFlipped(true); // Skip flip animation for pre-loaded results
            // Load saved interpretation if it exists
            if (existingResult.interpretation) {
              setInterpretation(existingResult.interpretation);
            }
            if (existingResult.savedToJournal) {
              setSavedToJournal(true);
            }
            setLoading(false);
            initializedRef.current = true;
            return;
          }
        }

        // In test mode, fetch all spreads and pick a random one
        // Otherwise, fetch today's spread
        let selectedSpread: DailySpread;
        if (isTestMode) {
          const allSpreads = await getAllDailySpreads();
          selectedSpread = allSpreads[Math.floor(Math.random() * allSpreads.length)];
        } else {
          selectedSpread = await getTodaysSpread();
        }

        setSpread(selectedSpread);
        const newCards = pickRandomCards(selectedSpread.positions.length);
        setCards(newCards);
        initializedRef.current = true;
      } catch (error) {
        console.error("Failed to fetch spread:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, setLocation, isTestMode]);

  // Fetch card overviews from guidebook when cards are set
  useEffect(() => {
    if (cards.length === 0) return;

    const fetchOverviews = async () => {
      try {
        const cardNames = cards.map((c) => c.name);
        const overviews = await getCardOverviews(cardNames);
        const overviewMap = new Map(overviews.map((o) => [o.name, o]));
        setCardOverviews(overviewMap);
      } catch (error) {
        console.error("Failed to fetch card overviews:", error);
      }
    };

    fetchOverviews();
  }, [cards]);

  // Get the appropriate overview for a card (upright or reversed)
  const getCardOverview = (cardName: string, reversed: boolean): string | undefined => {
    const overview = cardOverviews.get(cardName);
    if (!overview) return undefined;
    return reversed ? overview.overviewReverse || overview.overview : overview.overview;
  };

  const handleCardClick = async (index: number) => {
    // If card is already flipped, showcase it
    if (flippedCards.has(index)) {
      setShowcasedCardIndex(index);
      return;
    }

    // Don't allow flipping if result already saved (unless showcasing)
    if (result) return;

    const newFlipped = new Set(flippedCards);
    newFlipped.add(index);
    setFlippedCards(newFlipped);

    // If all cards are now flipped, save the result (unless in test mode)
    if (newFlipped.size === cards.length && spread && !saving) {
      trackEvent("/daily-spread/flipped_cards");
      if (!isTestMode) {
        setSaving(true);
        try {
          const savedResult = await saveDailySpreadResult(spread.id, cards);
          setResult(savedResult);
        } catch (error) {
          console.error("Failed to save result:", error);
        } finally {
          setSaving(false);
        }
      }
    }
  };

  const handleCloseShowcase = () => {
    setShowcasedCardIndex(null);
  };

  const handleGetOurTake = async () => {
    if (!spread || loadingInterpretation || interpretation) return;

    setLoadingInterpretation(true);
    trackEvent("/daily-spread/generated_interpretation");
    try {
      // Backend looks up meanings from GuidebookMeaning database
      const interpretationCards = cards.map((card, index) => ({
        position: spread.positions[index],
        cardName: card.name,
        reversed: card.reversed,
      }));

      // In non-test mode, pass the result ID to save the interpretation
      const resultId = !isTestMode ? result?.id : undefined;
      const interpretationText = await generateInterpretation(spread.title, interpretationCards, resultId);
      setInterpretation(interpretationText);
    } catch (error) {
      console.error("Error generating interpretation:", error);
    } finally {
      setLoadingInterpretation(false);
    }
  };

  const handleSaveToJournal = async () => {
    if (!result || savedToJournal || savingToJournal) return;

    setSavingToJournal(true);
    trackEvent("/daily-spread/saved_to_journal");
    try {
      await saveSpreadToJournal(result.id);
      setSavedToJournal(true);
    } catch (error) {
      console.error("Error saving to journal:", error);
    } finally {
      setSavingToJournal(false);
    }
  };

  const handleShare = async () => {
    if (!shareableRef.current || !spread || isSharing) return;

    setIsSharing(true);
    trackEvent("/daily-spread/pressed_share");

    try {
      const canvas = await html2canvas(shareableRef.current, {
        scale: 3,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null,
      });

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, "image/jpeg", 0.92);
      });

      if (!blob) {
        console.error("Failed to create image blob");
        setIsSharing(false);
        return;
      }

      const today = new Date();
      const dateStr = `${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}-${today.getFullYear()}`;
      const file = new File([blob], `moonlight-${dateStr}-${spread.title.toLowerCase().replace(/\s+/g, "-")}.jpg`, {
        type: "image/jpeg",
      });

      const shareText = "my daily tarot pull on Moonlight 🌙 https://app.moonlight.world";

      // Android: no Web Share API / blob downloads, so use the native bridge.
      const nativeBridge = getNativeShareBridge();
      if (nativeBridge?.shareImage) {
        const base64 = await blobToBase64(blob);
        setIsSharing(false);
        nativeBridge.shareImage(base64, shareText, file.name);
        return;
      }

      // Check if we're on mobile (share sheet works well there)
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

      if (isMobile && navigator.canShare && navigator.canShare({ files: [file] })) {
        // Mobile: hide loading before opening share sheet
        setIsSharing(false);
        await navigator.share({
          files: [file],
          text: shareText,
        });
      } else {
        // Desktop: download the image directly
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = file.name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      // User cancelled share or error occurred
      console.log("Share cancelled or failed:", error);
    } finally {
      setIsSharing(false);
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <PageContainer style={{ backgroundColor }} isIOSNative={isIOSNative} footer="none">
        <SimpleLoadingIndicator style={{ backgroundColor: appDarkMode ? "rgba(255, 255, 255, 0.35)" : "rgba(91, 91, 91, 0.5)" }} />
      </PageContainer>
    );
  }

  if (!spread) {
    return (
      <PageContainer style={{ backgroundColor }} isIOSNative={isIOSNative} footer="none">
        <Message>We're writing new spreads. Come back tomorrow!</Message>
      </PageContainer>
    );
  }

  const showcasedCard = showcasedCardIndex !== null ? cards[showcasedCardIndex] : null;
  const allCardsFlipped = cards.length > 0 && flippedCards.size === cards.length;

  // Responsive card sizing based on number of positions
  const numPositions = spread.positions.length;
  const cardWidth = numPositions >= 3 ? 80 : 120;
  const cardHeight = Math.round(cardWidth * 1.617); // Maintain aspect ratio

  const footerVariant = allCardsFlipped
    ? (isIOSNative ? "ios" : "standard")
    : "none";

  return (
    <PageContainer style={{ backgroundColor }} isIOSNative={isIOSNative} footer={footerVariant}>
      {!isIOSNative && (
        <HomeButton onClick={() => setLocation("/")} style={{ backgroundColor }}>
          <span className="arrow">&larr;</span>
          <span className="text">Home</span>
        </HomeButton>
      )}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <TitlePill>{spread.title}</TitlePill>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <SpreadContainer style={{ padding: numPositions === 1 ? "36px 92px" : "36px 30px" }}>
          <CardsGrid style={{ gridTemplateColumns: `repeat(${numPositions}, ${cardWidth}px)` }}>
            {spread.positions.map((position, index) => (
              <PositionLabel key={`label-${index}`}>{position}:</PositionLabel>
            ))}
            {spread.positions.map((_, index) => {
              // Only animate fly-in on fresh spreads, not returning visits
              const shouldAnimateFlyIn = shouldAnimateFlyInRef.current;
              return (
                <CardSlot
                  key={`slot-${index}`}
                  style={{ width: cardWidth - 6, height: cardHeight - 6 }}
                  initial={shouldAnimateFlyIn ? { borderColor: "rgba(0,0,0,1)" } : { borderColor: "rgba(0,0,0,0)" }}
                  animate={{ borderColor: "rgba(0,0,0,0)" }}
                  transition={{ duration: 0.3, delay: shouldAnimateFlyIn ? 0.7 + index * 0.25 + 0.4 : 0 }}
                >
                  <motion.div
                    key={`card-${index}`}
                    layoutId={`card-${index}`}
                    initial={shouldAnimateFlyIn ? {
                      x: 0,
                      y: 400,
                      rotate: (index % 2 === 0 ? -1 : 1) * (120 + index * 40),
                      opacity: 0,
                    } : undefined}
                    animate={{
                      x: 0,
                      y: 0,
                      rotate: 0,
                      opacity: 1,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 60,
                      damping: 14,
                      delay: shouldAnimateFlyIn ? 0.7 + index * 0.3 : 0,
                      opacity: {
                        duration: 0.15,
                      },
                      layout: {
                        type: "spring",
                        stiffness: 200,
                        damping: 25,
                      },
                    } as Transition}
                    style={{ justifySelf: "center" }}
                  >
                    <HoloCard
                      width={cardWidth}
                      height={cardHeight}
                      onClick={() => handleCardClick(index)}
                      disableInteraction={flippingCard === index}
                    >
                      <FlippableCard
                        flipped={flippedCards.has(index)}
                        frontImage={cards[index] ? `/images${rwsDeck.basePath}/${cards[index].artwork}` : ""}
                        backImage="/images/decks/rider-waite/cardback.jpg"
                        reversed={cards[index]?.reversed}
                        width={cardWidth}
                        height={cardHeight}
                        onFlipStart={() => setFlippingCard(index)}
                        onFlipEnd={() => setFlippingCard(null)}
                        initiallyFlipped={cardsPreFlipped}
                      />
                    </HoloCard>
                  </motion.div>
                </CardSlot>
              );
            })}
          </CardsGrid>
        </SpreadContainer>
      </motion.div>

      {/* Instruction text - shown before all cards are flipped */}
      <AnimatePresence>
        {!allCardsFlipped && (
          <motion.div
            key="instruction-text"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{
              duration: 0.4,
              delay: shouldAnimateFlyInRef.current ? 0.7 + (spread.positions.length - 1) * 0.3 + 0.5 : 0,
            }}
          >
            <InstructionText>Tap the cards whenever you're ready.</InstructionText>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Get our take button / interpretation - shown after all cards are flipped and saved */}
      {allCardsFlipped && !saving && (isTestMode || result) && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          style={{ marginTop: "-10px" }}
        >
          {interpretation ? (
            <InterpretationText>{interpretation}</InterpretationText>
          ) : loadingInterpretation ? (
            <LoadingContainer>
              <LoadingLabel>Checking our guidebook...</LoadingLabel>
              <SimpleLoadingIndicator style={{
                width: "18px",
                height: "18px",
                position: "static",
                backgroundColor: appDarkMode ? "rgba(255, 255, 255, 0.35)" : "rgba(0, 0, 0, 0.2)"
              }} />
            </LoadingContainer>
          ) : (
            <GetOurTakeButton onClick={handleGetOurTake}>
              Get Moonlight's take
            </GetOurTakeButton>
          )}
        </motion.div>
      )}

      {/* Save to journal button - shown after all cards are flipped and saved (not in test mode) */}
      {allCardsFlipped && !saving && !isTestMode && result && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, delay: 0.5 }}

        >
          {savedToJournal ? (
            <SavedToJournalLabel>✓ Saved to journal</SavedToJournalLabel>
          ) : savingToJournal ? (
            <SimpleLoadingIndicator style={{
              width: "14px",
              height: "14px",
              position: "static",
              backgroundColor: appDarkMode ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.15)"
            }} />
          ) : (
            <SaveToJournalButton onClick={handleSaveToJournal}>
              Add to journal
            </SaveToJournalButton>
          )}
        </motion.div>
      )}

      {/* Spacer to ensure content clears the fixed footer on mobile */}
      {allCardsFlipped && <div style={{ minHeight: "40px" }} />}

      {/* Footer - shown after all cards are flipped */}
      <AnimatePresence>
        {allCardsFlipped && (
          <Footer
            isIOSNative={isIOSNative}
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <BottomBarContent>
              <CountdownPill>
                <CountdownDot />
                new spread in {countdown}
              </CountdownPill>
              <FeedbackButtonWrapper>
                {/* Inline Tally feedback form */}
                <AnimatePresence>
                  {showFeedback && (
                    <FeedbackPopup
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 20, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <CloseButton onClick={() => setShowFeedback(false)}>
                        <img src="/images/exit.svg" alt="close" />
                      </CloseButton>
                      <FeedbackText>Spread of the day is new!<br />Let us know what you think.</FeedbackText>
                      <TallyEmbed
                        formId="mB6KVA"
                        email={user?.email}
                        username={user?.username}
                        name={user?.name}
                        resultId={result?.id}
                      />
                    </FeedbackPopup>
                  )}
                </AnimatePresence>
                <BottomButton onClick={() => setShowFeedback(!showFeedback)}>
                  <img src="/images/room/question.svg" alt="feedback" />
                </BottomButton>
              </FeedbackButtonWrapper>
              <BottomButton variant="dark" onClick={handleShare}>
                {isSharing ? (
                  <SimpleLoadingIndicator style={{ width: 18, height: 18, top: 0 }} />
                ) : (
                  <img src="/images/feedback.svg" alt="share" />
                )}
              </BottomButton>
            </BottomBarContent>
          </Footer>
        )}
      </AnimatePresence>

      {/* Showcase overlay */}
      <ShowcaseCardModal
        isOpen={showcasedCardIndex !== null && showcasedCard !== null}
        imageUrl={showcasedCard ? `/images${rwsDeck.basePath}/${showcasedCard.artwork}` : ""}
        reversed={showcasedCard?.reversed}
        layoutId={showcasedCardIndex !== null ? `card-${showcasedCardIndex}` : undefined}
        onClose={handleCloseShowcase}
        cardName={showcasedCard?.name}
        cardMeaning={showcasedCard ? getCardOverview(showcasedCard.name, showcasedCard.reversed) : undefined}
        backgroundColor={backgroundColor}
      />

      {/* Hidden shareable spread for screenshot */}
      <HiddenShareable>
        <ShareableSpread
          ref={shareableRef}
          title={spread.title}
          positions={spread.positions}
          cards={cards}
          interpretation={interpretation}
          backgroundColor={dailyColor}
        />
      </HiddenShareable>
    </PageContainer>
  );
};

// Styled components
const PageContainer = styled("div", {
  // CSS custom property for dynamic footer height
  "--footer-height": "0px",

  position: "fixed",
  inset: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  paddingTop: vh(22),
  // Dynamic padding-bottom that accounts for footer
  paddingBottom: "calc(20px + var(--footer-height))",
  paddingLeft: "20px",
  paddingRight: "20px",
  gap: "24px",
  overflowY: "auto",
  overflowX: "hidden",
  WebkitOverflowScrolling: "touch",
  touchAction: "pan-y",
  // Prevent layout shift when scrollbar appears
  scrollbarGutter: "stable",
  // same scrollbar as the rest of the app (see index.css)
  "&::-webkit-scrollbar": { width: "0.5rem", height: "0.5rem" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "$grayA200",
    borderRadius: "0.5rem",
    [`.${darkTheme.className} &`]: { backgroundColor: "$whiteA500" },
  },
  color: "$text",
  variants: {
    isIOSNative: {
      true: {
        paddingTop: vh(12),
      },
    },
    footer: {
      none: {},
      standard: {
        "--footer-height": "70px",
        "@mobile": {
          "--footer-height": "120px",
        },
      },
      ios: {
        "--footer-height": "calc(100px + env(safe-area-inset-bottom))",
      },
    },
  },
});

const HomeButton = styled("div", {
  position: "fixed",
  top: "60px",
  left: "20px",
  backgroundColor: "transparent",
  border: "1px solid #000",
  padding: "0 14px 0 12px",
  height: "38px",
  borderRadius: ".8rem",
  display: "flex",
  alignItems: "center",
  cursor: "pointer",
  zIndex: 100,
  transition: "opacity 0.2s ease",
  "@mobile": {
    top: "70px",
  },
  "&:hover": {
    opacity: "0.6",
  },
  "& .arrow": {
    marginRight: "8px",
    fontSize: "16px",
    fontWeight: "200",
  },
  "& .text": {
    fontSize: "12px",
  },
  [`.${darkTheme.className} &`]: { borderColor: "$border", color: "$text" },
});

const TitlePill = styled("div", {
  backgroundColor: "#1a1a1a",
  color: "#fff",
  padding: "10px 28px",
  borderRadius: "30px",
  fontSize: "12px",
  fontWeight: "400",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  [`.${darkTheme.className} &`]: { backgroundColor: "$inverseSurface", color: "$inverseText" },
});

const SpreadContainer = styled("div", {
  backgroundColor: "#f6f6f6",
  borderRadius: "24px",
  padding: "36px 30px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "20px",
  border: "1px solid #000",
  [`.${darkTheme.className} &`]: { backgroundColor: "$surface", borderColor: "$border" },
});

const CardsGrid = styled("div", {
  display: "grid",
  gap: "12px 24px",
  justifyItems: "center",
  alignItems: "end",
});

const CardSlot = styled(motion.div, {
  position: "relative",
  border: "1px dashed #000",
  borderRadius: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  [`.${darkTheme.className} &`]: { borderColor: "$textMuted" },
});

const PositionLabel = styled("div", {
  fontSize: "14px",
  fontWeight: "500",
  lineHeight: "1.1rem",
  color: "#333",
  textAlign: "center",
  [`.${darkTheme.className} &`]: { color: "$text" },
});

const Message = styled("div", {
  fontSize: "13px",
  fontWeight: "500",
});

const InstructionText = styled("div", {
  fontSize: "13px",
  textAlign: "center",
  maxWidth: "100%",
  lineHeight: "1.4",
});

const Footer = styled(motion.div, {
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "#f6f6f6",
  backdropFilter: "blur(12px)",
  padding: "12px 20px",
  paddingBottom: "calc(12px + env(safe-area-inset-bottom))",
  zIndex: 100,
  borderTop: "1px solid rgba(0, 0, 0, 0.4)",
  // Extend footer color below the safe area to cover iOS Safari overscroll
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "-100px",
    left: 0,
    right: 0,
    height: "100px",
    backgroundColor: "#f6f6f6",
  },
  variants: {
    isIOSNative: {
      true: {
        paddingBottom: "calc(40px + env(safe-area-inset-bottom))",
        paddingTop: "20px",
      },
    },
  },
  [`.${darkTheme.className} &`]: { backgroundColor: "$appBg", borderTopColor: "$subtleFill", "&::after": { backgroundColor: "$appBg" } },
});

const BottomBarContent = styled("div", {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  maxWidth: "400px",
  margin: "0 auto",
});

const BottomButton = styled("div", {
  width: "38px",
  height: "38px",
  borderRadius: "50%",
  backgroundColor: "rgba(0, 0, 0, 0.08)",
  border: "1px solid rgba(0, 0, 0, 0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  flexShrink: 0,
  position: "relative",
  "&:hover": {
    opacity: "0.8",
  },
  "& img": {
    width: "19px",
    height: "19px",
  },
  variants: {
    variant: {
      dark: {
        backgroundColor: "#000",
        "&:hover": {
          backgroundColor: "#222",
        },
        "& img": {
          filter: "invert(1)",
          opacity: 1,
        },
        [`.${darkTheme.className} &`]: { backgroundColor: "$inverseSurface", "& img": { filter: "none" } },
      },
    },
  },
  [`.${darkTheme.className} &`]: { backgroundColor: "$subtleFill", borderColor: "$textMuted", "& img": { filter: "invert(1)" } },
});

const CountdownPill = styled("div", {
  fontSize: "11px",
  color: "rgba(0, 0, 0, 0.9)",
  padding: "8px 16px",
  borderRadius: "100px",
  textTransform: "uppercase",
  fontVariantNumeric: "tabular-nums",
  display: "flex",
  alignItems: "center",
  gap: "6px",
  whiteSpace: "nowrap",
  backgroundColor: "rgba(0, 0, 0, 0.05)",
  marginRight: "5px",
  [`.${darkTheme.className} &`]: { color: "$text", backgroundColor: "$subtleFill" },
});

const CountdownDot = styled("div", {
  width: "5px",
  height: "5px",
  borderRadius: "50%",
  backgroundColor: "#000000ff",
  [`.${darkTheme.className} &`]: { backgroundColor: "$text" },
});

const GetOurTakeButton = styled("button", {
  fontSize: "13px",
  textDecoration: "underline",
  textAlign: "center",
  backgroundColor: "transparent",
  color: "#000",
  border: "none",
  cursor: "pointer",
  marginTop: "6px",
  "&:hover": {
    opacity: "0.7",
  },
  [`.${darkTheme.className} &`]: { color: "$text" },
});

const InterpretationText = styled("p", {
  fontSize: "13px",
  color: "#000",
  textAlign: "center",
  maxWidth: "360px",
  margin: "5px auto 0px",
  lineHeight: "1.4",
  fontWeight: "500",
  padding: "0 20px 0",
  [`.${darkTheme.className} &`]: { color: "$text" },
});

const SaveToJournalButton = styled("button", {
  fontSize: "12px",
  textAlign: "center",
  backgroundColor: "#fff",
  color: "#000",
  border: "1px solid #000",
  borderRadius: "11px",
  padding: "9px 14px",
  marginTop: "-4px",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: "0.7",
  },
  [`.${darkTheme.className} &`]: { backgroundColor: "$surface", color: "$text", borderColor: "$border" },
});

const SavedToJournalLabel = styled("div", {
  fontSize: "12px",
  color: "rgba(0, 0, 0, 0.5)",
  textAlign: "center",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
});

const LoadingContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
  marginTop: "6px",
});

const LoadingLabel = styled("div", {
  fontSize: "13px",
  color: "rgb(0, 0, 0, 0.8)",
  [`.${darkTheme.className} &`]: { color: "$textMuted" },
});

const HiddenShareable = styled("div", {
  position: "absolute",
  left: "-9999px",
  top: 0,
  pointerEvents: "none",
});

const FeedbackButtonWrapper = styled("div", {
  position: "relative",
});

const FeedbackPopup = styled(motion.div, {
  position: "absolute",
  bottom: "100%",
  right: "-120px",
  width: "280px",
  height: "230px",
  backgroundColor: "#fff",
  borderRadius: "16px",
  marginBottom: "20px",
  overflow: "hidden",
  border: "1px solid rgba(0, 0, 0, 0.8)",

  "@mobile": {
    right: "-70px",
  },
  [`.${darkTheme.className} &`]: { backgroundColor: "$surface", borderColor: "$textMuted" },
});

const FeedbackText = styled("p", {
  fontSize: "12px",
  color: "#000",
  textAlign: "center",
  padding: "40px 30px 6px",
  marginBottom: "-36px",
  lineHeight: "1.4",
  [`.${darkTheme.className} &`]: { color: "$text" },
});

const CloseButton = styled("button", {
  position: "absolute",
  top: "8px",
  right: "8px",
  background: "none",
  border: "none",
  padding: "4px",
  cursor: "pointer",
  transition: "opacity 0.2s ease",
  "&:hover": {
    opacity: 1,
  },
  "& img": {
    width: "20px",
    height: "20px",
    display: "block",
  },
  [`.${darkTheme.className} &`]: { "& img": { filter: "invert(1)" } },
});

// Inline Tally embed component
const TallyEmbed = ({
  formId,
  email,
  username,
  name,
  resultId,
}: {
  formId: string;
  email?: string;
  username?: string;
  name?: string;
  resultId?: string;
}) => {
  useEffect(() => {
    const TALLY_SCRIPT_URL = "https://tally.so/widgets/embed.js";

    // Check if Tally is already loaded
    if (typeof (window as any).Tally !== 'undefined') {
      (window as any).Tally.loadEmbeds();
      return;
    }

    // Check if script is already in the DOM
    if (document.querySelector(`script[src="${TALLY_SCRIPT_URL}"]`)) {
      // Script exists but Tally not ready yet, wait for it
      setTimeout(() => {
        if (typeof (window as any).Tally !== 'undefined') {
          (window as any).Tally.loadEmbeds();
        }
      }, 100);
      return;
    }

    // Load Tally script for the first time
    const script = document.createElement("script");
    script.src = TALLY_SCRIPT_URL;
    script.onload = () => {
      if (typeof (window as any).Tally !== 'undefined') {
        (window as any).Tally.loadEmbeds();
      }
    };
    document.body.appendChild(script);
  }, []);

  const params = new URLSearchParams();
  params.set("alignLeft", "1");
  params.set("hideTitle", "1");
  params.set("transparentBackground", "1");
  params.set("dynamicHeight", "1");
  if (email) params.set("email", email);
  if (username) params.set("username", username);
  if (name) params.set("name", name);
  if (resultId) params.set("spread", resultId);
  params.set("feature", "spread");

  return (
    <div style={{ padding: "0 8px 8px" }}>
      <iframe
        data-tally-src={`https://tally.so/embed/${formId}?${params.toString()}`}
        loading="lazy"
        width="100%"
        height="280"
        style={{
          border: "none",
          background: "transparent",
        }}
        title="Feedback form"
      />
    </div>
  );
};
